import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

/* =========================
   CONFIG CACHE
========================= */
const MENU_CACHE_KEY = "menu_cache_v2";
const MENU_TTL_MS = 1000 * 60 * 60 * 24;

function readMenuCache() {
  try {
    const raw = localStorage.getItem(MENU_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.expiresAt || parsed.expiresAt < Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeMenuCache(data) {
  try {
    localStorage.setItem(
      MENU_CACHE_KEY,
      JSON.stringify({
        ...data,
        expiresAt: Date.now() + MENU_TTL_MS,
      })
    );
  } catch {}
}

export default function FbGetMenuHome() {
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [dataByCat, setDataByCat] = useState({});
  const [categoriaAtiva, setCategoriaAtiva] = useState(null);
  const [loading, setLoading] = useState(true);

  const fecharTudo = () => {
    setMenuOpen(false);
    setSubMenuOpen(false);
    setCategoriaAtiva(null);
  };

  /* =========================
     RESOLVER TIPO
  ========================= */
  const resolveType = (categoria, subcategoria, produtoType) => {
    if (produtoType) return produtoType.toLowerCase();

    const subType =
      dataByCat[categoria]?.subs?.[subcategoria]?.type?.toLowerCase();
    if (subType) return subType;

    const catType = categories.find((c) => c.name === categoria)?.type;
    if (catType) return catType.toLowerCase();

    return "produtos";
  };

  /* =========================
     NAVEGAÇÃO
  ========================= */
  const callwindows = (subcategoria, produtoNome, produtoType) => {
    const type = resolveType(categoriaAtiva, subcategoria, produtoType);
    const prefix = type === "agenda" ? "Agenda" : "Produtos";

    navigate(`/${prefix}/${categoriaAtiva}/${subcategoria}/${produtoNome}`);
    fecharTudo();
  };

  const callcat = (categoria, subcategoria) => {
    const type = resolveType(categoria, subcategoria);
    const prefix = type === "agenda" ? "Agenda" : "Produtos";

    navigate(`/${prefix}/${categoria}/${subcategoria}`);
  
    fecharTudo();
  };

  /* =========================
     CARREGAMENTO INICIAL
  ========================= */
  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      const cache = readMenuCache();

      if (cache && mounted) {
        setCategories(cache.categories);
        setDataByCat(cache.dataByCat);
        setLoading(false);
        return;
      }

      try {
        const snap = await getDocs(collection(db, "Menu"));

        const cats = snap.docs
          .map((doc) => ({
            name: doc.id,
            type: doc.data()?.type?.toLowerCase() || "produtos",
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        if (!mounted) return;

        setCategories(cats);
        writeMenuCache({ categories: cats, dataByCat: {} });
      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();
    return () => (mounted = false);
  }, []);

  /* =========================
     ABRIR SUBMENU RÁPIDO
  ========================= */
  const abrirSubmenu = async (categoria) => {
    setCategoriaAtiva(categoria);
    setSubMenuOpen(true);

    if (dataByCat[categoria]) return;

    try {
      const subSnap = await getDocs(
        collection(db, `Menu/${categoria}/subcategoria`)
      );

      const subs = {};

      await Promise.all(
        subSnap.docs.map(async (subDoc) => {
          const subcategoria = subDoc.id;
          const subType = subDoc.data()?.type?.toLowerCase();

          const produtosSnap = await getDocs(
            collection(
              db,
              `Menu/${categoria}/subcategoria/${subcategoria}/produtos`
            )
          );

          const produtos = produtosSnap.docs.map((p) => ({
            id: p.id,
            ...p.data(),
          }));

          subs[subcategoria] = {
            type: subType,
            produtos,
          };
        })
      );

      const updated = {
        ...dataByCat,
        [categoria]: { subs },
      };

      setDataByCat(updated);
      writeMenuCache({ categories, dataByCat: updated });
    } catch (err) {
      console.error(err);
    }
  };

  /* =========================
     JSX
  ========================= */
  return (
    <div>
      <header className="topbar">
        <button className="menu-btn" onClick={() => setMenuOpen(true)}>
          <MenuIcon /> Menu
        </button>
      </header>

      {menuOpen && <div className="overlay" onClick={fecharTudo} />}

      <aside className={`side-menu ${menuOpen ? "open" : ""}`}>
        <div className="menu-header">
          <span>{loading ? "A carregar..." : "Categorias"}</span>
          <CloseIcon onClick={fecharTudo} />
        </div>

        <ul className="menu-list">
          {categories.map((cat) => (
            <li key={cat.name} onClick={() => abrirSubmenu(cat.name)}>
              <span>{cat.name}</span>
              <ArrowRightIcon />
            </li>
          ))}
        </ul>
      </aside>

      <aside className={`sub-menu ${subMenuOpen ? "open" : ""}`}>
        <button className="back" onClick={() => setSubMenuOpen(false)}>
          ← Voltar
        </button>

        {categoriaAtiva &&
          dataByCat[categoriaAtiva] &&
          Object.entries(dataByCat[categoriaAtiva].subs).map(
            ([subcategoria, data]) => (
              <div key={subcategoria} className="submenu-section">
                <h4 onClick={() => callcat(categoriaAtiva, subcategoria)}>
                  {subcategoria}
                </h4>

                {data.produtos?.length > 0 ? (
                  <div className="produtos-grid">
                    {data.produtos.map((produto) => (
                      <div
                        key={produto.id}
                        className="produto-card"
                        onClick={() =>
                          callwindows(
                            subcategoria,
                            produto.Produto,
                            produto.type
                          )
                        }
                      >
                        {produto.Produto}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="muted">Sem produtos</span>
                )}
              </div>
            )
          )}
      </aside>
    </div>
  );
}